import axios, { AxiosResponse, AxiosRequestConfig } from 'axios'
import { SESSION_KEYS } from '@/services/browserStorage'

type ExtraConfig = Omit<AxiosRequestConfig, 'url' | 'method' | 'data'>

import {
  accessDenied,
  loginAgain,
  serverUnreachable
} from '@/services/userAlerts'
import { compareStrings } from '@/services/functions'

export const apiPortal = axios.create({
  baseURL: process.env.API_URL,
  withCredentials: true
})

export const apiPortalRevokeVet = axios.create({
  baseURL: process.env.API_URL,
  withCredentials: true
})

apiPortalRevokeVet.interceptors.request.use(function (request) {
  request.headers['X-Application'] = 'vet'
  return request
})

apiPortalRevokeVet.interceptors.response.use(
  function (response) {
    if (!response) {
      console.log('TESTE server unreachable')
    }
    return response
  },

  function (error) {
    if (error.response) {
      if (error.response.status === 0) {
        console.log('TESTE network error', error)
      } else if (
        (error.response.status === 401 || error.response.status === 403) &&
        error.config.url !== ENDPOINTS.sign_out
      ) {
        console.log('TESTE access denied', error)
      }
      return error.response
    }
    return error
  }
)

apiPortal.interceptors.request.use(function (request) {
  const appSpecificEndpoints = [
    ENDPOINTS.refresh_token,
    ENDPOINTS.sign_out,
    ENDPOINTS.get_organization_info,
    ENDPOINTS.update_user_info,
    ENDPOINTS.upload_user_img,
    ENDPOINTS.get_tree,
    ENDPOINTS.sign_in,
    ENDPOINTS.sign_up,
    ENDPOINTS.signup_confirm,
    ENDPOINTS.set_new_password,
    ENDPOINTS.request_password,
    ENDPOINTS.confirm_password,
    ENDPOINTS.change_password,
    ENDPOINTS.save_collection,
    ENDPOINTS.get_collections,
    ENDPOINTS.duplicate_collection,
    ENDPOINTS.create_folder,
    ENDPOINTS.move_collection,
    ENDPOINTS.delete_collection,
    ENDPOINTS.get_collection,
    ENDPOINTS.tree_collections,
    ENDPOINTS.list_folders_available_to_add,
    ENDPOINTS.rename_collection,
    ENDPOINTS.get_solucx_nps,
    ENDPOINTS.get_content_rating,
    ENDPOINTS.rate_content,
    ENDPOINTS.share_collection_by_users_ids,
    ENDPOINTS.unshare_collection_by_users_ids,
    ENDPOINTS.remove_collections_notifications,
    ENDPOINTS.check_collections_notifications,
    ENDPOINTS.create_collection_share_token,
    ENDPOINTS.add_collection_by_share_token,
    ENDPOINTS.get_my_infos,
    ENDPOINTS.get_slides,
    ENDPOINTS.create_slide_marker,
    ENDPOINTS.get_user_onboarding,
    ENDPOINTS.save_user_onboarding,
    ENDPOINTS.content_suggestion,
    ENDPOINTS.suggestions,
    ENDPOINTS.user_recent_contents,
    ENDPOINTS.week_top_contents,
    ENDPOINTS.next_releases,
    ENDPOINTS.increment_content_release_clicks,
    ENDPOINTS.survey_can_answer,
    ENDPOINTS.survey_answer
  ]

  //  unitySpecificEndpoints = [ENDPOINTS.duplicate_collection, ENDPOINTS.move_collection]

  if (appSpecificEndpoints.includes(request.url)) {
    request.headers['X-Application'] = 'bioatlas'
  }

  /* if (unitySpecificEndpoints.includes(request.url)) {
    request.headers['x-unity-user-agent'] = 'web'
  } */

  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    const fromURL = params.get('x-unity-user-agent')
    const fromStorage = sessionStorage.getItem(SESSION_KEYS.unity_user_agent)
    const unityUserAgent = fromURL || fromStorage
    if (unityUserAgent) {
      request.headers['x-unity-user-agent'] = unityUserAgent
    }
  }

  // console.log(request)
  return request
})

apiPortal.interceptors.response.use(
  function (response) {
    // console.log(response)

    if (!response) {
      // console.log('TESTE server unreachable')
      serverUnreachable()
      return
    }

    return response
  },

  function (error) {
    const status = error?.response?.status

    if (error?.config?.headers && error?.code && status !== 404) {
      console.error(
        '-> ' +
          JSON.stringify({
            status,
            url: error?.config?.url,
            name: error?.name,
            code: error?.code,
            headers: error?.config?.headers
          }) +
          ' ' +
          error?.message
      )
    }

    if (error.response) {
      if (status === 0) {
        loginAgain()
        return
      } else if (
        (status === 401 || status === 403) &&
        error.config.url !== ENDPOINTS.sign_out
      ) {
        console.error('-> error:', error)
        const msg = `status: ${status}<br>${error.response.config?.url}`
        accessDenied(msg)
        return
      }

      return error.response
    }

    return error
  }
)

export const sendPostRequest = <T = any>(
  URL: string,
  data: object,
  optionalHeaders: Record<string, string> | null = null,
  extraConfig: ExtraConfig = {}
) => {
  let headers: Record<string, string> = {
    'Content-Type': 'application/json;charset=UTF-8'
  }
  if (optionalHeaders) headers = { ...headers, ...optionalHeaders }

  const config: AxiosRequestConfig = {
    headers,
    ...extraConfig
  }

  return apiPortal.post<T>(URL, JSON.stringify(data), config)
}

export const sendGetRequest = <T = any>(
  URL: string,
  optionalHeaders: Record<string, string> | null = null,
  extraConfig: ExtraConfig = {}
) => {
  let headers: Record<string, string> = {}
  if (optionalHeaders) headers = { ...headers, ...optionalHeaders }

  const config: AxiosRequestConfig = {
    headers,
    ...extraConfig
  }

  return apiPortal.get<T>(URL, config)
}

export const sendRevokeVetRequest = (URL: string) => {
  const headers = {}
  const config = {
    parse: true,
    headers: headers
  }

  return apiPortalRevokeVet.get(URL, config)
}

interface ResponseState {
  name: number | string
  callback: (data: any) => void
}

export const handleResponseStates = (
  response: AxiosResponse,
  listedStates: Array<ResponseState>
) => {
  // console.log('TESTE handleResponseStates response', response)
  const matchingState = listedStates.find(state => {
    if (typeof state.name === 'number') {
      return state.name === response.status
    } else {
      return (
        compareStrings(state.name, response.data.status) ||
        compareStrings(state.name, response.data.msg)
      )
    }
  })

  if (matchingState) {
    // console.log('TESTE handleResponseStates matchingState', matchingState)
    matchingState.callback(response.data)
    return
  }

  //UNHANDLED EXCEPTION
  const error = new Error('Uncaught Exception')
  //@ts-expect-error add error data
  error.data = response
  throw error
}

export const ENDPOINTS = {
  sign_in: 'auth/v3/signin',
  sign_out: 'auth/revoke-token',
  refresh_token: 'auth/refresh-token',
  auth_ip: 'auth/v3/ip-login',
  auth_sso: 'auth/v3/sso',

  set_new_password: 'users/set-new-password',
  change_password: 'users/change-password',
  request_password: 'users/forgot-password',
  confirm_password: 'users/forgot-password-confirm',

  sign_up: 'users/signup',
  signup_confirm: 'users/signup-confirm',

  get_my_infos: 'users/v3/get-my-infos',
  get_user_onboarding: 'users/v3/get-user-onboarding',
  save_user_onboarding: 'users/v3/save-user-onboarding',
  update_user_info: 'users/v4/update-user',
  upload_user_img: 'users/v4/upload-user-image',
  get_organization_info: 'admin/v3/get-organization-info',
  get_tree: 'portal/v3/get-tree',
  get_class_info: 'admin/v3/get-class-info',
  get_content_info: 'portal/v3/get-content-info',

  get_slides: 'portal/v3/get-slides',
  create_slide_marker: 'portal/v3/create-slide-marker',
  get_solucx_nps: 'users/v3/get-solucx-nps',

  get_collections: 'portal/v3/list-collections',
  save_collection: 'portal/v3/save-collection',
  move_collection: 'portal/v3/move-collection',
  tree_collections: 'portal/v3/tree-collections',
  list_folders_available_to_add: '/portal/v3/list-folders-available-to-add',
  get_collection: 'portal/v3/get-collection',
  rename_collection: 'portal/v3/rename-collection',
  delete_collection: 'portal/v3/delete-collection',
  duplicate_collection: 'portal/v3/duplicate-collection',
  create_folder: 'portal/v3/create-folder',
  update_folder: 'portal/v3/update-folder',
  delete_folder: 'portal/v3/delete-folder',

  list_related_users: 'users/v3/list-related-users',
  rate_content: 'portal/v3/rate-content',
  get_content_rating: 'portal/v3/get-content-rating',
  check_content_license: 'admin/v3/check-content-license',
  pre_auth_sso: 'auth/v3/pre-auth-sso',

  create_collection_share_token: 'portal/v3/create-collection-share-token',
  add_collection_by_share_token: 'portal/v3/add-collection-by-share-token',
  share_collection_by_users_ids: 'portal/v3/share-collection',
  unshare_collection_by_users_ids: 'portal/v3/unshare-collection',
  remove_collections_notifications:
    'portal/v3/remove-collections-notifications',
  check_collections_notifications: 'portal/v3/check-collections-notifications',

  content_suggestion: 'portal/v3/content-suggestion',
  suggestions: 'portal/v3/suggestions',
  user_recent_contents: 'portal/v3/user-recent-contents',
  week_top_contents: 'portal/v3/week-top-contents',
  next_releases: 'admin/v3/list-content-releases',
  increment_content_release_clicks: 'admin/v3/increment-content-release-clicks',

  search_contents: 'portal/v3/search',

  survey_can_answer: 'portal2/v3/survey/can-answer',
  survey_answer: 'portal2/v3/survey/answer'
}
